import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { formatCurrency, formatDate, getDaysUntil } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gift, Plus, Calendar, DollarSign, Package, LogOut, User, Crown } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newList, setNewList] = useState({
    name: '',
    event_type: 'christmas',
    event_date: '',
    budget: 0
  });

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const response = await api.get('/lists');
      setLists(response.data);
    } catch (error) {
      toast.error('Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  const createList = async (e) => {
    e.preventDefault();
    try {
      await api.post('/lists', newList);
      toast.success('List created!');
      setShowCreateDialog(false);
      setNewList({ name: '', event_type: 'christmas', event_date: '', budget: 0 });
      fetchLists();
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  const totalBudget = lists.reduce((sum, list) => sum + list.budget, 0);
  const totalSpent = lists.reduce((sum, list) => sum + list.total_spent, 0);
  const totalGifts = lists.reduce((sum, list) => sum + list.items_count - list.purchased_count, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Gift className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold gradient-text">Gifty</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.subscription_tier === 'premium' && (
              <div className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                <Crown className="w-4 h-4" />
                Premium
              </div>
            )}
            {user?.subscription_tier === 'elite' && (
              <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-900 rounded-full text-sm font-medium">
                <Crown className="w-4 h-4" />
                Elite
              </div>
            )}
            <Button variant="ghost" onClick={() => navigate('/recipients')} data-testid="recipients-nav-button">
              Recipients
            </Button>
            {user?.subscription_tier === 'elite' && (
              <>
                <Button variant="ghost" onClick={() => navigate('/birthday-tracker')} data-testid="birthday-tracker-nav-button">
                  Birthday Tracker
                </Button>
                <Button variant="ghost" onClick={() => navigate('/reports')} data-testid="reports-nav-button">
                  Reports
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => navigate('/subscription')} data-testid="subscription-nav-button">
              Subscription
            </Button>
            <Button variant="ghost" onClick={() => navigate('/settings')} data-testid="settings-nav-button">
              <User className="w-5 h-5" />
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            Welcome back, <span className="gradient-text">{user?.name}!</span> 🎄
          </h1>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Lists</p>
                  <p className="text-3xl font-bold text-red-600">{lists.length}</p>
                </div>
                <Calendar className="w-12 h-12 text-red-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Budget Status</p>
                  <p className="text-3xl font-bold text-green-600">
                    {formatCurrency(totalSpent)} / {formatCurrency(totalBudget)}
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Gifts Remaining</p>
                  <p className="text-3xl font-bold text-amber-600">{totalGifts}</p>
                </div>
                <Package className="w-12 h-12 text-amber-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lists Section */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Your Gift Lists</h2>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="create-list-button">
                <Plus className="w-4 h-4 mr-2" />
                Create New List
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-list-dialog">
              <DialogHeader>
                <DialogTitle>🎁 Create New Gift List</DialogTitle>
              </DialogHeader>
              <form onSubmit={createList} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">List Name</label>
                  <Input
                    placeholder="Christmas 2025"
                    value={newList.name}
                    onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                    required
                    data-testid="list-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Event Type</label>
                  <Select value={newList.event_type} onValueChange={(val) => setNewList({ ...newList, event_type: val })}>
                    <SelectTrigger data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="christmas">Christmas</SelectItem>
                      <SelectItem value="birthday">Birthday</SelectItem>
                      <SelectItem value="hanukkah">Hanukkah</SelectItem>
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="anniversary">Anniversary</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Event Date</label>
                  <Input
                    type="date"
                    value={newList.event_date}
                    onChange={(e) => setNewList({ ...newList, event_date: e.target.value })}
                    required
                    data-testid="event-date-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Budget (optional)</label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={newList.budget || ''}
                    onChange={(e) => setNewList({ ...newList, budget: parseFloat(e.target.value) || 0 })}
                    data-testid="budget-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="submit-list-button">
                  🎄 Create List
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Lists Grid */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : lists.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <Gift className="w-16 h-16 text-red-600 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">🎁 No gift lists yet!</h3>
              <p className="text-gray-600 mb-4">Ready to get organized for the holidays?</p>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => setShowCreateDialog(true)}
                data-testid="create-first-list-button"
              >
                🎄 Create Your First List
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lists.map((list) => (
              <Card
                key={list.id}
                className="glass-card hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/lists/${list.id}`)}
                data-testid={`list-card-${list.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>{list.event_type === 'christmas' ? '🎄' : '🎂'}</span>
                    <span>{list.name}</span>
                  </CardTitle>
                  <p className="text-sm text-gray-600">{formatDate(list.event_date)}</p>
                  {getDaysUntil(list.event_date) > 0 && (
                    <p className="text-xs text-amber-600">{getDaysUntil(list.event_date)} days away</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Budget</span>
                        <span className="font-semibold">
                          {formatCurrency(list.total_spent)} / {formatCurrency(list.budget)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            list.budget > 0 && list.total_spent / list.budget > 0.8
                              ? 'bg-red-600'
                              : 'bg-green-600'
                          }`}
                          style={{ width: `${Math.min((list.total_spent / list.budget) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>{list.items_count} gifts</span>
                      <span>
                        {list.purchased_count} purchased • {list.items_count - list.purchased_count} remaining
                      </span>
                    </div>
                    <Button className="w-full" variant="outline" data-testid={`view-list-button-${list.id}`}>
                      View Details →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
